// FTEDN — Voting helpers (validation, results, vote power)

export type VoteChoice = 'up' | 'down';
export type VoteCategory = 'product' | 'scent' | 'packaging' | 'ingredient' | 'texture' | 'concept';

export const CATEGORY_ICONS: Record<string, string> = {
  product: '🧴', scent: '🌸', packaging: '📦',
  ingredient: '🧬', texture: '✨', concept: '💡',
};

export const CATEGORY_LIST: VoteCategory[] = ['product', 'scent', 'packaging', 'ingredient', 'texture', 'concept'];

/** Calculate vote power multiplier based on user level */
export function getVotePower(level: number): number {
  if (level >= 20) return 5;
  if (level >= 10) return 3;
  if (level >= 5) return 2;
  return 1;
}

/** Calculate XP reward for a vote action */
export function getVoteXP(isFirstVoteToday: boolean, votePower: number): number {
  const base = 15;
  const firstBonus = isFirstVoteToday ? 10 : 0;
  return (base + firstBonus) * Math.min(votePower, 5);
}

/** Determine if a card qualifies as "hot" */
export function isHotCard(votes: number, createdAt: string): boolean {
  const age = Date.now() - new Date(createdAt).getTime();
  const dayMs = 1000 * 60 * 60 * 24;

  // Hot if high votes within first 7 days, or very high votes anytime
  if (votes >= 200) return true;
  if (age <= 7 * dayMs && votes >= 50) return true;
  if (age <= 2 * dayMs && votes >= 20) return true;
  return false;
}

export interface VoteResultBar {
  label: string;
  count: number;
  percentage: number;
  winner: boolean;
}

/** Calculate vote results for display as a bar chart */
export function calculateResults(likes: number, totalVotes: number): VoteResultBar[] {
  if (totalVotes === 0) {
    return [
      { label: 'For', count: 0, percentage: 0, winner: false },
      { label: 'Against', count: 0, percentage: 0, winner: false },
    ];
  }

  const dislikes = totalVotes - likes;
  const forPct = Math.round((likes / totalVotes) * 100);
  const againstPct = 100 - forPct;

  return [
    { label: 'For', count: likes, percentage: forPct, winner: forPct >= againstPct },
    { label: 'Against', count: dislikes, percentage: againstPct, winner: againstPct > forPct },
  ];
}

/** Validate an idea submission */
export function validateIdea(idea: { title: string; description: string; category: string }): { valid: true } | { valid: false; error: string } {
  if (!idea.title.trim()) {
    return { valid: false, error: 'Title is required' };
  }
  if (idea.title.trim().length < 3) {
    return { valid: false, error: 'Title must be at least 3 characters' };
  }
  if (idea.title.length > 100) {
    return { valid: false, error: 'Title must be under 100 characters' };
  }
  if (!idea.description.trim()) {
    return { valid: false, error: 'Description is required' };
  }
  if (idea.description.trim().length < 10) {
    return { valid: false, error: 'Description must be at least 10 characters' };
  }
  if (idea.description.length > 2000) {
    return { valid: false, error: 'Description must be under 2000 characters' };
  }
  if (!idea.category || !CATEGORY_LIST.includes(idea.category as VoteCategory)) {
    return { valid: false, error: 'Select a valid category' };
  }
  return { valid: true };
}

/** Group cards by category with counts */
export function groupByCategory(cards: { category: string }[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const card of cards) {
    counts[card.category] = (counts[card.category] || 0) + 1;
  }
  return counts;
}
